/** Shared score → level / color presentation helpers (0–100 scale). */

/**
 * Canonical score → maturity level. Uses the spec-defined bands
 * (0-19 L1, 20-39 L2, 40-59 L3, 60-79 L4, 80-100 L5 — see
 * .speads/specs/discovery-quality-scoring/plan.md and .claude/commands/assess.md)
 * so the UI agrees with the levels the evaluator assigns to each dimension.
 */
export function levelFromScore(score: number): string {
  if (score >= 80) return 'L5';
  if (score >= 60) return 'L4';
  if (score >= 40) return 'L3';
  if (score >= 20) return 'L2';
  return 'L1';
}

/**
 * Whether a score sits at the top maturity level (L5 - Optimizing, >= 80).
 * Used to gate the #1-row confetti celebration so only genuinely top-tier
 * leaders earn a burst.
 */
export function isTopMaturity(score: number): boolean {
  return levelFromScore(score) === 'L5';
}

/**
 * Level → color token (with hex fallback) for score displays. Kept distinct from
 * LEVEL_BAR_COLORS, which emits the bare token for the stacked bars; scoreColor
 * preserves the hex fallback its callers (and tests) rely on.
 */
const SCORE_LEVEL_COLORS: Record<string, string> = {
  L1: 'var(--genai-sem-level-l1, #64748b)',
  L2: 'var(--genai-sem-level-l2, #ca8a04)',
  L3: 'var(--genai-sem-level-l3, #3b82f6)',
  L4: 'var(--genai-sem-level-l4, #8b5cf6)',
  L5: 'var(--genai-sem-level-l5, #22c55e)',
};

/**
 * Single source of truth for score → color. Returns the level's CSS token so it
 * tracks the palette in tokens.css. Derives the level from levelFromScore — the
 * one band ladder — so a colored score always matches its level badge and the
 * 80/60/40/20 thresholds live in exactly one place. Green is the highest level
 * (L5); L4 is purple.
 */
export function scoreColor(score: number): string {
  return SCORE_LEVEL_COLORS[levelFromScore(score)]!;
}

/** Stacked-bar color per level — green (L5) is highest; L4 is purple.
 *  Bound to the --genai-sem-level-l* tokens (single source of truth) rather than
 *  duplicating the hex values; the bar then round-trips through the token system. */
export const LEVEL_BAR_COLORS: Record<string, string> = {
  L1: 'var(--genai-sem-level-l1)',
  L2: 'var(--genai-sem-level-l2)',
  L3: 'var(--genai-sem-level-l3)',
  L4: 'var(--genai-sem-level-l4)',
  L5: 'var(--genai-sem-level-l5)',
};

/** Canonical maturity-level order, low → high (L1 leftmost, L5 rightmost). */
export const LEVEL_ORDER = ['L1', 'L2', 'L3', 'L4', 'L5'];

/** Human-readable name per maturity level (single source of truth). */
export const LEVEL_NAMES: Record<string, string> = {
  L1: 'Initial',
  L2: 'Developing',
  L3: 'Defined',
  L4: 'Managed',
  L5: 'Optimizing',
};

/**
 * Format a maturity level for display as "L4 - Managed". Unknown values (e.g.
 * 'SKIPPED' or an empty string) pass through unchanged so callers can render
 * non-level states without special-casing.
 */
export function levelLabel(level: string): string {
  const name = LEVEL_NAMES[level];
  return name ? `${level} - ${name}` : level;
}

/**
 * Normalize a maturity level to canonical form: "2" or 2 → "L2", "L2"/"l2" → "L2".
 * Anything outside L1–L5 (e.g. undefined, 'SKIPPED', '') returns '' so callers can
 * treat an unscored/absent level as "no badge".
 */
export function normalizeLevel(raw: unknown): string {
  const s = String(raw ?? '');
  if (/^[1-5]$/.test(s)) return `L${s}`;
  if (/^L[1-5]$/i.test(s)) return s.toUpperCase();
  return '';
}

/**
 * Full CSS badge class for a maturity level: "badge badge-lN" for L1–L5, and
 * "badge badge-info" for any other value (unknown, 'SKIPPED', ''). Single source
 * of truth for the level → badge map; every component imports this rather than
 * redefining its own copy. Returns the complete class string, so call sites must
 * NOT prepend their own "badge " literal.
 */
export function levelBadgeClass(level: string): string {
  const map: Record<string, string> = {
    L1: 'badge-l1',
    L2: 'badge-l2',
    L3: 'badge-l3',
    L4: 'badge-l4',
    L5: 'badge-l5',
  };
  return `badge ${map[level] ?? 'badge-info'}`;
}
